<template>
  <b-card no-body>
    <b-card-header class="pb-0">
      <b-card-title>Vehicle Expiry Reminders</b-card-title>
    </b-card-header>

    <b-card-body>
      <div v-if="!hasAccess" class="text-center py-4">
        <p class="text-muted">Access denied. Admin or HR user type required.</p>
      </div>
      
      <div v-else-if="loading" class="d-flex justify-content-center py-4">
        <b-spinner variant="primary" label="Loading..." />
      </div>

      <div v-else-if="error" class="text-center py-4">
        <p class="text-danger">{{ error }}</p>
      </div>

      <div v-else-if="!expiryData || expiryData.length === 0" class="text-center py-4">
        <p class="text-muted">No vehicles expiring in 15 days.</p>
      </div>

      <div v-else class="overflow-auto" style="min-height: 5cm; max-height: 10cm">
        <table class="table" style="min-width: 800px !important">
          <thead class="text thead-sticky">
            <tr>
              <th class="tableTh">No.</th>
              <th class="tableTh">Vehicle Name</th>
              <th class="tableTh">Vehicle No.</th>
              <th class="tableTh">Owner Name</th>
              <th class="tableTh">Type</th>
              <th class="tableTh">Expiry Date</th>
              <th class="tableTh">Days Remaining</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in expiryData" :key="`${item.id}-${item.type}`" style="height: 60px">
              <td class="tableTd">{{ index + 1 }}</td>
              <td class="tableTd">{{ item.vehiclename }}</td>
              <td class="tableTd">{{ item.vehicleno }}</td>
              <td class="tableTd">{{ item.owner_name || '-' }}</td>
              <td class="tableTd">
                <b-badge :variant="item.type === 'insurance' ? 'warning' : 'info'">
                  {{ item.type === 'insurance' ? 'Insurance Expiry' : 'PUC Expiry' }}
                </b-badge>
              </td>
              <td class="tableTd">{{ item.expiryDate }}</td>
              <td class="tableTd">
                <b-badge variant="danger">{{ item.daysRemaining }} days</b-badge>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BSpinner,
  BBadge,
} from "bootstrap-vue";
import axios from '@/components/axios';

export default {
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      expiryData: [],
      loading: false,
      error: null,
      baseApi: process.env.VUE_APP_APIENDPOINT,
      hasAccess: false,
    };
  },
  mounted() {
    // Check user type before making API call
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    const userType = userData.type;
    
    // Check user type (case-insensitive)
    const typeLower = (userType || "").toLowerCase();
    
    // Only fetch if user type is admin or contains "hr"
    if (typeLower === "admin" || typeLower.includes("hr")) {
      this.hasAccess = true;
      this.fetchVehicleExpiryReminders();
    } else {
      this.hasAccess = false;
      this.error = "Access denied. Admin or HR user type required.";
      console.warn("Vehicle Expiry Reminders: User type is", userType, "- Access denied");
    }
  },
  methods: {
    async fetchVehicleExpiryReminders() {
      this.loading = true;
      this.error = null;

      // Get fresh token on each request
      const accessToken = localStorage.getItem("accessToken");
      
      if (!accessToken) {
        this.error = "Authentication required.";
        this.loading = false;
        return;
      }

      try {
        const requestOptions = {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          url: `${this.baseApi}/vehicle-expiry-reminders`,
        };

        const response = await axios(requestOptions);

        if (response.data && response.data.status === 200) {
          this.expiryData = response.data.data || [];
        } else if (response.data && response.data.status === 404) {
          // Handle 403/404 access denied
          if (response.data.message && (response.data.message.includes("HR") || response.data.message.includes("Access denied"))) {
            this.error = "Access denied. Admin or HR user type required.";
          } else {
            this.error = response.data.message || "Error fetching vehicle expiry reminders.";
          }
          this.expiryData = [];
        } else {
          this.error = response.data?.message || "Error fetching vehicle expiry reminders.";
          this.expiryData = [];
        }
      } catch (error) {
        console.error("Error fetching vehicle expiry reminders:", error);
        console.error("Error response:", error.response);
        
        if (error.response) {
          if (error.response.status === 403 || error.response.status === 404) {
            // If access denied, check if it's a user type issue
            const errorMessage = error.response.data?.message || "";
            if (errorMessage.includes("HR") || errorMessage.includes("Access denied")) {
              this.error = "Access denied. Admin or HR user type required.";
              console.warn("API returned access denied. User type may not match backend requirements.");
            } else {
              this.error = errorMessage || "Access denied. Admin or HR user type required.";
            }
          } else if (error.response.data && error.response.data.message) {
            this.error = error.response.data.message;
          } else {
            this.error = "Error fetching vehicle expiry reminders.";
          }
        } else {
          this.error = "Error fetching vehicle expiry reminders.";
        }
        this.expiryData = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.tableTh,
.tableTd {
  max-width: 150px;
  padding: 10px 10px 10px 5px !important;
  text-align: center;
  vertical-align: middle;
}

.tableTh {
  font-size: 11px !important;
  font-weight: 600;
  background-color: #f8f8f8;
}

.thead-sticky {
  position: sticky;
  top: 0px;
  z-index: 10;
  background-color: #f8f8f8;
}
</style>

